<template>
  <div class="category-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Notification Toast -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Page Title -->
        <div>
          <div class="page-title">CATEGORY</div>
          <div class="page-subtitle">Manage metal categories, product items, and design variations</div>
        </div>

        <!-- Actions: Search + Expand/Collapse + Add Category -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <div class="search-input-wrap">
            <v-text-field
              v-model="searchKeyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search category, item..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <v-btn
            outlined
            color="#475569"
            class="btn-toggle-expand text-capitalize font-weight-medium"
            @click="toggleAllExpand()"
          >
            <v-icon left small color="#64748b">
              {{ ExpandStatus2 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
            {{ ExpandStatus2 ? 'Close All' : 'Open All' }}
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1 font-weight-bold"
            @click="openAddCategory()"
          >
            <v-icon left small>mdi-plus</v-icon>
            Category
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 3 Simple KPI Cards (Clean & Minimal) -->
    <div class="kpi-grid mb-4">
      <div class="kpi-card">
        <div class="kpi-label">CATEGORIES</div>
        <div class="kpi-value">{{ totalCategories }}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">PRODUCT ITEMS</div>
        <div class="kpi-value">{{ totalItems }}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">DESIGNS</div>
        <div class="kpi-value">{{ totalDesigns }}</div>
      </div>
    </div>

    <!-- Master Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Table Header -->
      <div class="table-header-row px-4 py-3 d-flex align-center">
        <div style="width: 50px;" class="th-label">S.NO</div>
        <div style="flex: 2; min-width: 180px;" class="th-label">NAME</div>
        <div style="width: 140px;" class="th-label">CODE</div>
        <div style="flex: 2; min-width: 180px;" class="th-label">DESCRIPTION</div>
        <div style="width: 140px;" class="text-center th-label">ACTION</div>
        <div style="width: 45px;" class="text-center th-label"></div>
      </div>

      <!-- Categories List -->
      <div v-if="filteredCategoryList && filteredCategoryList.length > 0">
        <div
          v-for="(cat, i) in filteredCategoryList"
          :key="cat._id || i"
          class="category-block"
        >
          <!-- LEVEL 1: CATEGORY ROW -->
          <div
            class="category-row px-4 py-3 d-flex align-center"
            :class="{ 'category-row-active': cat.isexpand }"
            @click="toggleCategoryExpand(cat)"
          >
            <!-- Serial Number -->
            <div style="width: 50px;" class="td-text text-muted font-weight-medium">
              {{ i + 1 }}
            </div>

            <!-- Name -->
            <div style="flex: 2; min-width: 180px;" class="td-text font-weight-bold d-flex align-center">
              <span>{{ cat.name || '-' }}</span>
              <span class="count-badge ml-2" v-if="cat.items && cat.items.length > 0">
                {{ cat.items.length }} items
              </span>
            </div>

            <!-- Code -->
            <div style="width: 140px;" class="td-text">
              <span class="code-text">{{ cat.code || '-' }}</span>
            </div>

            <!-- Description -->
            <div style="flex: 2; min-width: 180px;" class="td-text text-muted">
              {{ cat.description || '-' }}
            </div>

            <!-- Actions -->
            <div style="width: 140px;" class="d-flex align-center justify-center actions-cell" @click.stop>
              <!-- Edit -->
              <v-btn icon small title="Edit Category" @click.stop="openEditCategory(cat)">
                <v-icon small color="#475569">mdi-file-edit-outline</v-icon>
              </v-btn>
              <!-- Delete -->
              <v-btn icon small title="Delete Category" @click.stop="openDeleteCategory(cat._id)">
                <v-icon small color="#475569">mdi-delete-outline</v-icon>
              </v-btn>
              <!-- Add Item -->
              <v-btn icon small title="Add Item" @click.stop="openAddItem(cat)">
                <v-icon small color="#9a6b27">mdi-plus</v-icon>
              </v-btn>
            </div>

            <!-- Expand Chevron -->
            <div style="width: 45px;" class="text-center" @click.stop="toggleCategoryExpand(cat)">
              <v-icon small :class="{ 'rotate-180': cat.isexpand }" color="#64748b">
                mdi-chevron-down
              </v-icon>
            </div>
          </div>

          <!-- LEVEL 2: ITEMS CONTAINER (When Category Expanded) -->
          <div v-if="cat.isexpand" class="items-container">
            <div v-if="cat.items && cat.items.length > 0">
              <!-- Item Sub-Header -->
              <div class="sub-header-row px-4 py-2 d-flex align-center">
                <div style="width: 50px;" class="sub-th-label">ITEM #</div>
                <div style="flex: 2; min-width: 180px;" class="sub-th-label">ITEM NAME</div>
                <div style="width: 140px;" class="sub-th-label">CODE</div>
                <div style="width: 140px;" class="sub-th-label">STOCK ALERT</div>
                <div style="flex: 2; min-width: 180px;" class="sub-th-label">DESCRIPTION</div>
                <div style="width: 140px;" class="text-center sub-th-label">ACTION</div>
                <div style="width: 45px;" class="text-center sub-th-label"></div>
              </div>

              <!-- Item Rows -->
              <div
                v-for="(item, j) in cat.items"
                :key="item._id || j"
                class="item-block"
              >
                <div
                  class="item-row px-4 py-2 d-flex align-center"
                  :class="{ 'item-row-active': item.isexpand }"
                  @click="toggleItemExpand(item)"
                >
                  <!-- S.No -->
                  <div style="width: 50px;" class="td-sub-text text-muted">
                    {{ i + 1 }}.{{ j + 1 }}
                  </div>

                  <!-- Name -->
                  <div style="flex: 2; min-width: 180px;" class="td-sub-text font-weight-bold d-flex align-center">
                    <span>{{ item.name || '-' }}</span>
                    <span class="count-badge ml-2" v-if="item.design && item.design.length > 0">
                      {{ item.design.length }} designs
                    </span>
                  </div>

                  <!-- Code -->
                  <div style="width: 140px;" class="td-sub-text">
                    <span class="code-text">{{ item.code || '-' }}</span>
                  </div>

                  <!-- Low Stock Alert -->
                  <div style="width: 140px;" class="td-sub-text">
                    <span v-if="item.lowStockThreshold && Number(item.lowStockThreshold) > 0" class="text-slate-800">
                      &lt; {{ item.lowStockThreshold }} gm
                    </span>
                    <span v-else class="text-muted">—</span>
                  </div>

                  <!-- Description -->
                  <div style="flex: 2; min-width: 180px;" class="td-sub-text text-muted">
                    {{ item.description || '-' }}
                  </div>

                  <!-- Actions -->
                  <div style="width: 140px;" class="d-flex align-center justify-center actions-cell" @click.stop>
                    <v-btn icon small title="Edit Item" @click.stop="openEditItem(item)">
                      <v-icon small color="#475569">mdi-file-edit-outline</v-icon>
                    </v-btn>
                    <v-btn icon small title="Delete Item" @click.stop="openDeleteItem(item._id)">
                      <v-icon small color="#475569">mdi-delete-outline</v-icon>
                    </v-btn>
                    <v-btn icon small title="Add Design" @click.stop="openAddDesign(item)">
                      <v-icon small color="#9a6b27">mdi-plus</v-icon>
                    </v-btn>
                  </div>

                  <!-- Chevron -->
                  <div style="width: 45px;" class="text-center" @click.stop="toggleItemExpand(item)">
                    <v-icon v-if="item.design && item.design.length > 0" small :class="{ 'rotate-180': item.isexpand }" color="#64748b">
                      mdi-chevron-down
                    </v-icon>
                  </div>
                </div>

                <!-- LEVEL 3: DESIGNS CONTAINER (When Item Expanded) -->
                <div v-if="item.isexpand && item.design && item.design.length > 0" class="designs-container">
                  <!-- Design Sub-Header -->
                  <div class="design-header-row px-4 py-2 d-flex align-center">
                    <div style="width: 60px;" class="sub-th-label">DESIGN #</div>
                    <div style="flex: 2; min-width: 170px;" class="sub-th-label">DESIGN NAME</div>
                    <div style="width: 140px;" class="sub-th-label">CODE</div>
                    <div style="width: 100px;" class="sub-th-label">TYPE</div>
                    <div style="width: 140px;" class="sub-th-label">HSN</div>
                    <div style="flex: 2; min-width: 150px;" class="sub-th-label">DESCRIPTION</div>
                    <div style="width: 140px;" class="text-center sub-th-label">ACTION</div>
                    <div style="width: 45px;"></div>
                  </div>

                  <!-- Design Rows -->
                  <div
                    v-for="(des, k) in item.design"
                    :key="des._id || k"
                    class="design-row px-4 py-2 d-flex align-center"
                  >
                    <div style="width: 60px;" class="td-sub-text text-muted">
                      {{ i + 1 }}.{{ j + 1 }}.{{ k + 1 }}
                    </div>
                    <div style="flex: 2; min-width: 170px;" class="td-sub-text font-weight-medium">
                      {{ des.name || '-' }}
                    </div>
                    <div style="width: 140px;" class="td-sub-text">
                      <span class="code-text">{{ des.code || '-' }}</span>
                    </div>
                    <div style="width: 100px;" class="td-sub-text">
                      <span v-if="des.countOrSplit" class="simple-tag">{{ des.countOrSplit }}</span>
                      <span v-else class="text-muted">—</span>
                    </div>
                    <div style="width: 140px;" class="td-sub-text">
                      <span v-if="des.HSN">{{ getHsnDisplay(des.HSN) }}</span>
                      <span v-else class="text-muted">—</span>
                    </div>
                    <div style="flex: 2; min-width: 150px;" class="td-sub-text text-muted">
                      {{ des.description || '-' }}
                    </div>
                    <div style="width: 140px;" class="d-flex align-center justify-center actions-cell">
                      <v-btn icon small title="Edit Design" @click.stop="openEditDesign(des)">
                        <v-icon small color="#475569">mdi-file-edit-outline</v-icon>
                      </v-btn>
                      <v-btn icon small title="Delete Design" @click.stop="openDeleteDesign(des._id)">
                        <v-icon small color="#475569">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                    <div style="width: 45px;"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty Items State -->
            <div v-else class="pa-4 text-center text-muted" style="font-size: 13px;">
              No items in this category.
              <a href="javascript:void(0)" class="ml-1 text-decoration-none font-weight-bold" style="color: #9a6b27;" @click="openAddItem(cat)">
                + Add Item
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="pa-8 text-center text-muted">
        <div style="font-size: 14px;">No categories found</div>
      </div>
    </v-card>

    <!-- ============================================== -->
    <!-- MODAL 1: ADD CATEGORY                          -->
    <!-- ============================================== -->
    <v-dialog v-model="catadddialogue" persistent max-width="540px">
      <v-card class="pa-6 rounded-lg">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="dialog-title">Add Category</h2>
          <v-btn icon small @click="catadddialogue = false; resetForm1()">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4"></v-divider>
        <v-form ref="addcat1" v-model="addcat1" @submit.prevent="AddCategoryValidation()">
          <v-layout wrap>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Name *</label>
              <v-text-field
                v-model="catname"
                placeholder="e.g. GOLD"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Code *</label>
              <v-text-field
                v-model="catcode"
                placeholder="e.g. G1"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 px-2 pb-3>
              <label class="form-label">Description</label>
              <v-text-field
                v-model="catdescription"
                placeholder="Description"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <div class="d-flex justify-end mt-4" style="gap: 8px;">
            <v-btn text class="text-capitalize" @click="catadddialogue = false; resetForm1()">Cancel</v-btn>
            <v-btn dark color="#9a6b27" class="text-capitalize" @click="AddCategoryValidation()">Save</v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 2: EDIT CATEGORY                         -->
    <!-- ============================================== -->
    <v-dialog v-model="cateditdialoge" persistent max-width="540px">
      <v-card class="pa-6 rounded-lg" v-if="curitem1">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="dialog-title">Edit Category</h2>
          <v-btn icon small @click="cateditdialoge = false">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4"></v-divider>
        <v-layout wrap>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Name *</label>
            <v-text-field
              v-model="curitem1.name"
              outlined
              dense
              color="#9a6b27"
              :rules="[rules.required]"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Code *</label>
            <v-text-field
              v-model="curitem1.code"
              outlined
              dense
              color="#9a6b27"
              :rules="[rules.required]"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 px-2 pb-3>
            <label class="form-label">Description</label>
            <v-text-field
              v-model="curitem1.description"
              outlined
              dense
              color="#9a6b27"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <div class="d-flex justify-end mt-4" style="gap: 8px;">
          <v-btn text class="text-capitalize" @click="cateditdialoge = false">Cancel</v-btn>
          <v-btn dark color="#9a6b27" class="text-capitalize" @click="cateditValidation()">Save</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 3: DELETE CATEGORY                       -->
    <!-- ============================================== -->
    <v-dialog v-model="catdeldialoge" max-width="400px">
      <v-card class="pa-6 text-center rounded-lg">
        <h3 class="dialog-title mb-2">Delete Category</h3>
        <p class="text-muted mb-4" style="font-size: 13px;">Are you sure you want to delete this category?</p>
        <div class="d-flex justify-center" style="gap: 10px;">
          <v-btn text class="text-capitalize" @click="catdeldialoge = false">Cancel</v-btn>
          <v-btn dark color="#ef4444" class="text-capitalize" @click="delcategory()">Delete</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 4: ADD ITEM                              -->
    <!-- ============================================== -->
    <v-dialog v-model="itemadddialogue" persistent max-width="580px">
      <v-card class="pa-6 rounded-lg">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="dialog-title">Add Item</h2>
          <v-btn icon small @click="itemadddialogue = false; resetForm2()">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4"></v-divider>
        <v-form ref="addcat2" v-model="addcat2" @submit.prevent="AddItemValidation()">
          <v-layout wrap>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Name *</label>
              <v-text-field
                v-model="itemname"
                placeholder="e.g. Ring"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Code *</label>
              <div class="d-flex align-center">
                <span class="prefix-box mr-1">{{ code9 }}/</span>
                <v-text-field
                  v-model="itemcode"
                  placeholder="code"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  style="flex: 1;"
                ></v-text-field>
              </div>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Low Stock Alert (grams, 0 = off)</label>
              <v-text-field
                v-model="itemLowStockThreshold"
                type="number"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Description</label>
              <v-text-field
                v-model="itemdescription"
                placeholder="Description"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <div class="d-flex justify-end mt-4" style="gap: 8px;">
            <v-btn text class="text-capitalize" @click="itemadddialogue = false; resetForm2()">Cancel</v-btn>
            <v-btn dark color="#9a6b27" class="text-capitalize" @click="AddItemValidation()">Save</v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 5: EDIT ITEM                             -->
    <!-- ============================================== -->
    <v-dialog v-model="itemeditdialoge" persistent max-width="580px">
      <v-card class="pa-6 rounded-lg" v-if="curitem2">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="dialog-title">Edit Item</h2>
          <v-btn icon small @click="itemeditdialoge = false">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4"></v-divider>
        <v-layout wrap>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Name *</label>
            <v-text-field
              v-model="curitem2.name"
              outlined
              dense
              color="#9a6b27"
              :rules="[rules.required]"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Code *</label>
            <div class="d-flex align-center">
              <span class="prefix-box mr-1">{{ code4 }}/</span>
              <v-text-field
                v-model="curitem2.code"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                style="flex: 1;"
              ></v-text-field>
            </div>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Low Stock Alert (grams, 0 = off)</label>
            <v-text-field
              v-model="curitem2.lowStockThreshold"
              type="number"
              outlined
              dense
              color="#9a6b27"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Description</label>
            <v-text-field
              v-model="curitem2.description"
              outlined
              dense
              color="#9a6b27"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <div class="d-flex justify-end mt-4" style="gap: 8px;">
          <v-btn text class="text-capitalize" @click="itemeditdialoge = false">Cancel</v-btn>
          <v-btn dark color="#9a6b27" class="text-capitalize" @click="EditItemValidation()">Save</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 6: DELETE ITEM                           -->
    <!-- ============================================== -->
    <v-dialog v-model="itemdeldialoge" max-width="400px">
      <v-card class="pa-6 text-center rounded-lg">
        <h3 class="dialog-title mb-2">Delete Item</h3>
        <p class="text-muted mb-4" style="font-size: 13px;">Are you sure you want to delete this item?</p>
        <div class="d-flex justify-center" style="gap: 10px;">
          <v-btn text class="text-capitalize" @click="itemdeldialoge = false">Cancel</v-btn>
          <v-btn dark color="#ef4444" class="text-capitalize" @click="delitem()">Delete</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 7: ADD DESIGN                            -->
    <!-- ============================================== -->
    <v-dialog v-model="designadddialogue" persistent max-width="600px">
      <v-card class="pa-6 rounded-lg">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="dialog-title">Add Design</h2>
          <v-btn icon small @click="designadddialogue = false; resetForm3()">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4"></v-divider>
        <v-form ref="addcat3" v-model="addcat3" @submit.prevent="AdddesignValidation()">
          <v-layout wrap>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Name *</label>
              <v-text-field
                v-model="designname"
                placeholder="e.g. Design 1"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Code *</label>
              <div class="d-flex align-center">
                <span class="prefix-box mr-1">{{ code8 }}/</span>
                <v-text-field
                  v-model="designcode"
                  placeholder="code"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  style="flex: 1;"
                ></v-text-field>
              </div>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-label">Type *</label>
              <v-select
                v-model="designcountOrSplit"
                :items="type"
                placeholder="Select Type"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm6 px-2 pb-3>
              <div class="d-flex align-center justify-space-between mb-1">
                <label class="form-label mb-0">HSN *</label>
                <a href="javascript:void(0)" class="text-caption font-weight-bold" style="color: #9a6b27;" @click.prevent="openAddHsn()">
                  + Add HSN
                </a>
              </div>
              <v-select
                v-model="designHSN"
                :items="HSNList"
                item-text="hsnCode"
                item-value="_id"
                placeholder="Select HSN"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-select>
            </v-flex>
            <v-flex xs12 px-2 pb-3>
              <label class="form-label">Description</label>
              <v-text-field
                v-model="designdescription"
                placeholder="Description"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <div class="d-flex justify-end mt-4" style="gap: 8px;">
            <v-btn text class="text-capitalize" @click="designadddialogue = false; resetForm3()">Cancel</v-btn>
            <v-btn dark color="#9a6b27" class="text-capitalize" @click="AdddesignValidation()">Save</v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 8: EDIT DESIGN                           -->
    <!-- ============================================== -->
    <v-dialog v-model="designeditdialoge" persistent max-width="600px">
      <v-card class="pa-6 rounded-lg" v-if="curitem3">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="dialog-title">Edit Design</h2>
          <v-btn icon small @click="designeditdialoge = false">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4"></v-divider>
        <v-layout wrap>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Name *</label>
            <v-text-field
              v-model="curitem3.name"
              outlined
              dense
              color="#9a6b27"
              :rules="[rules.required]"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Code *</label>
            <div class="d-flex align-center">
              <span class="prefix-box mr-1">{{ coded4 }}/</span>
              <v-text-field
                v-model="curitem3.code"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                style="flex: 1;"
              ></v-text-field>
            </div>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">Type *</label>
            <v-select
              v-model="curitem3.countOrSplit"
              :items="type"
              outlined
              dense
              color="#9a6b27"
              :rules="[rules.required]"
              hide-details="auto"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm6 px-2 pb-3>
            <label class="form-label">HSN *</label>
            <v-select
              v-model="curitem3.HSN"
              :items="HSNList"
              item-text="hsnCode"
              item-value="_id"
              outlined
              dense
              color="#9a6b27"
              :rules="[rules.required]"
              hide-details="auto"
            ></v-select>
          </v-flex>
          <v-flex xs12 px-2 pb-3>
            <label class="form-label">Description</label>
            <v-text-field
              v-model="curitem3.description"
              outlined
              dense
              color="#9a6b27"
              hide-details="auto"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <div class="d-flex justify-end mt-4" style="gap: 8px;">
          <v-btn text class="text-capitalize" @click="designeditdialoge = false">Cancel</v-btn>
          <v-btn dark color="#9a6b27" class="text-capitalize" @click="editdesignValidation()">Save</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 9: DELETE DESIGN                         -->
    <!-- ============================================== -->
    <v-dialog v-model="designdeldialoge" max-width="400px">
      <v-card class="pa-6 text-center rounded-lg">
        <h3 class="dialog-title mb-2">Delete Design</h3>
        <p class="text-muted mb-4" style="font-size: 13px;">Are you sure you want to delete this design?</p>
        <div class="d-flex justify-center" style="gap: 10px;">
          <v-btn text class="text-capitalize" @click="designdeldialoge = false">Cancel</v-btn>
          <v-btn dark color="#ef4444" class="text-capitalize" @click="deldesign()">Delete</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 10: QUICK ADD HSN CODE                   -->
    <!-- ============================================== -->
    <v-dialog v-model="addHSNdialog" persistent max-width="460px">
      <v-form ref="addcat4" v-model="addcat4" @submit.prevent="AddHsn()">
        <v-card class="pa-6 rounded-lg">
          <div class="d-flex align-center justify-space-between mb-4">
            <h2 class="dialog-title">Add HSN Code</h2>
            <v-btn icon small @click="addHSNdialog = false; resetForm4()">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>
          <v-divider class="mb-4"></v-divider>
          <v-layout wrap>
            <v-flex xs12 pb-3>
              <label class="form-label">HSN Code *</label>
              <v-text-field
                v-model="newHSNcode"
                placeholder="e.g. 711319"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 pb-3>
              <label class="form-label">HSN Name *</label>
              <v-text-field
                v-model="newHSNname"
                placeholder="Name"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 pb-3>
              <label class="form-label">Slab Percentage (%) *</label>
              <v-text-field
                v-model="newHSNpercent"
                type="number"
                placeholder="e.g. 3"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 pb-3>
              <label class="form-label">Description</label>
              <v-text-field
                v-model="newHSNdescription"
                placeholder="Description"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <div class="d-flex justify-end mt-4" style="gap: 8px;">
            <v-btn text class="text-capitalize" @click="addHSNdialog = false; resetForm4()">Cancel</v-btn>
            <v-btn dark color="#9a6b27" class="text-capitalize" @click="AddHsn()">Save</v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "NestedDataTable",
  props: {
    msg: String,
  },
  data() {
    return {
      testup: false,
      testup1: false,
      testup2: false,
      addcat1: false,
      addcat2: false,
      addcat3: false,
      addcat4: false,
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg14: null,
      page: 1,
      Pagelength: null,
      jewelleryId: null,
      adminList: [],
      rolesList: [],
      name: null,
      description: null,
      code: null,
      phoneNumber: null,
      positionList: null,
      categoryList: [],
      adddialog: false,
      HSNList: [],
      type: ["Split", "Count"],

      searchKeyword: "",

      // Category
      catadddialogue: false,
      cateditdialoge: false,
      curitem1: {},
      catdeldialoge: false,
      curItemHSN: {
        hsnCode: "",
        name: "",
        hsnDescription: "",
        gstSlab1Percentage: "",
      },
      curId1: "",
      catname: "",
      catcode: "",
      catdescription: "",
      categoryList1: [],

      // Item
      itemadddialogue: false,
      itemname: "",
      itemcode: "",
      itemdescription: "",
      itemLowStockThreshold: 0,
      catid: "",
      code9: "",
      curitem2: {},
      curId2: "",
      itemeditdialoge: false,
      itemdeldialoge: false,
      code1: null,
      code2: null,
      code3: null,
      code4: null,
      codeNew: null,

      // Design
      designadddialogue: false,
      designname: "",
      designcode: "",
      designdescription: "",
      designHSN: "",
      catid2: "",
      code8: "",
      curitem3: {},
      curId3: "",
      designcountOrSplit: "",
      designeditdialoge: false,
      designdeldialoge: false,
      coded1: null,
      coded2: null,
      coded3: null,
      coded4: null,
      codedNew: null,

      // HSN
      addHSNdialog: false,
      newHSNpercent: null,
      newHSNcode: null,
      newHSNname: null,
      newHSNdescription: null,

      rules: {
        required: (value) => !!value || "Field is required",
        counter: (value) => (value && value.length <= 20) || "Max 20 characters",
      },

      ExpandStatus2: false,
    };
  },
  mounted() {
    this.getCategory1();
    this.getHSN();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    totalCategories() {
      return this.categoryList ? this.categoryList.length : 0;
    },
    totalItems() {
      if (!this.categoryList) return 0;
      return this.categoryList.reduce((acc, cat) => {
        return acc + ((cat.items && cat.items.length) || 0);
      }, 0);
    },
    totalDesigns() {
      if (!this.categoryList) return 0;
      let count = 0;
      this.categoryList.forEach((cat) => {
        if (cat.items) {
          cat.items.forEach((it) => {
            if (it.design) {
              count += it.design.length;
            }
          });
        }
      });
      return count;
    },
    filteredCategoryList() {
      if (!this.categoryList) return [];
      const query = (this.searchKeyword || "").trim().toLowerCase();
      if (!query) return this.categoryList;

      return this.categoryList
        .map((cat) => {
          const catMatches =
            (cat.name && cat.name.toLowerCase().includes(query)) ||
            (cat.code && cat.code.toLowerCase().includes(query)) ||
            (cat.description && cat.description.toLowerCase().includes(query));

          const matchingItems = (cat.items || []).filter((item) => {
            const itemMatches =
              (item.name && item.name.toLowerCase().includes(query)) ||
              (item.code && item.code.toLowerCase().includes(query)) ||
              (item.description && item.description.toLowerCase().includes(query));

            const matchingDesigns = (item.design || []).filter((des) => {
              return (
                (des.name && des.name.toLowerCase().includes(query)) ||
                (des.code && des.code.toLowerCase().includes(query)) ||
                (des.description && des.description.toLowerCase().includes(query)) ||
                (des.countOrSplit && des.countOrSplit.toLowerCase().includes(query))
              );
            });

            return itemMatches || matchingDesigns.length > 0;
          });

          if (catMatches || matchingItems.length > 0) {
            const clonedCat = { ...cat };
            if (!catMatches && matchingItems.length > 0) {
              clonedCat.items = matchingItems;
            }
            clonedCat.isexpand = true;
            if (clonedCat.items) {
              clonedCat.items.forEach((item) => {
                item.isexpand = true;
              });
            }
            return clonedCat;
          }
          return null;
        })
        .filter(Boolean);
    },
  },
  methods: {
    toggleAllExpand() {
      this.ExpandStatus2 = !this.ExpandStatus2;
      this.setExpandStatus(this.categoryList, this.ExpandStatus2);
    },
    openAll() {
      this.ExpandStatus2 = true;
      this.setExpandStatus(this.categoryList, true);
    },
    closeAll() {
      this.ExpandStatus2 = false;
      this.setExpandStatus(this.categoryList, false);
    },
    setExpandStatus(list, status) {
      if (!list) return;
      for (const item of list) {
        this.$set(item, "isexpand", status);
        if (item.items) {
          this.setExpandStatus(item.items, status);
        }
        if (item.design) {
          for (const subItem of item.design) {
            this.$set(subItem, "isexpand", status);
          }
        }
      }
    },
    toggleCategoryExpand(cat) {
      this.$set(cat, "isexpand", !cat.isexpand);
    },
    toggleItemExpand(item) {
      this.$set(item, "isexpand", !item.isexpand);
    },

    resetForm1() {
      if (this.$refs.addcat1) this.$refs.addcat1.reset();
      this.catname = "";
      this.catcode = "";
      this.catdescription = "";
    },
    resetForm2() {
      if (this.$refs.addcat2) this.$refs.addcat2.reset();
      this.itemname = "";
      this.itemcode = "";
      this.itemdescription = "";
      this.itemLowStockThreshold = 0;
    },
    resetForm3() {
      if (this.$refs.addcat3) this.$refs.addcat3.reset();
      this.designname = "";
      this.designcode = "";
      this.designdescription = "";
      this.designcountOrSplit = "";
      this.designHSN = "";
    },
    resetForm4() {
      if (this.$refs.addcat4) this.$refs.addcat4.reset();
      this.newHSNcode = "";
      this.newHSNname = "";
      this.newHSNpercent = null;
      this.newHSNdescription = "";
    },

    // Category CRUD
    getCategory1() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/all/category/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          count: 20,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.categoryList = response.data.data;
            this.Pagelength = Math.ceil(response.data.count / 20);
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    openAddCategory() {
      this.resetForm1();
      this.catadddialogue = true;
    },
    openEditCategory(cat) {
      this.curitem1 = JSON.parse(JSON.stringify(cat));
      this.cateditdialoge = true;
    },
    openDeleteCategory(id) {
      this.curId1 = id;
      this.catdeldialoge = true;
    },
    AddCategoryValidation() {
      if (!this.catname) {
        this.msg = "Please enter category name";
        this.showsnackbar = true;
      } else if (!this.catcode) {
        this.msg = "Please enter category code";
        this.showsnackbar = true;
      } else {
        this.AddCategory();
      }
    },
    cateditValidation() {
      if (!this.curitem1.name) {
        this.msg = "Please enter category name";
        this.showsnackbar = true;
      } else if (!this.curitem1.code) {
        this.msg = "Please enter category code";
        this.showsnackbar = true;
      } else {
        this.editcat();
      }
    },
    AddCategory() {
      this.appLoading = true;
      axios({
        url: "/category/add",
        method: "POST",
        data: {
          name: this.catname,
          description: this.catdescription,
          code: this.catcode,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.catadddialogue = false;
            this.resetForm1();
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    editcat() {
      this.appLoading = true;
      axios({
        url: "/category/edit",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curitem1._id,
          name: this.curitem1.name,
          code: this.curitem1.code,
          description: this.curitem1.description,
        },
      })
        .then((response) => {
          this.cateditdialoge = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.curitem1 = {};
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.getCategory1();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    delcategory() {
      this.appLoading = true;
      axios({
        url: "/category/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId1,
        },
      })
        .then((response) => {
          this.catdeldialoge = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.curId1 = "";
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    // Item CRUD
    openAddItem(cat) {
      this.code9 = cat.code;
      this.catid = cat._id;
      this.resetForm2();
      this.itemadddialogue = true;
    },
    openEditItem(item) {
      this.curitem2 = JSON.parse(JSON.stringify(item));
      this.itemeditdialoge = true;
      this.editcode(this.curitem2.code);
    },
    openDeleteItem(id) {
      this.curId2 = id;
      this.itemdeldialoge = true;
    },
    AddItemValidation() {
      if (!this.itemname) {
        this.msg = "Please enter item name";
        this.showsnackbar = true;
      } else if (!this.itemcode) {
        this.msg = "Please enter item code";
        this.showsnackbar = true;
      } else {
        this.AddItem();
      }
    },
    EditItemValidation() {
      if (!this.curitem2.name) {
        this.msg = "Please enter item name";
        this.showsnackbar = true;
      } else if (!this.curitem2.code) {
        this.msg = "Please enter item code";
        this.showsnackbar = true;
      } else {
        this.editcode2();
      }
    },
    AddItem() {
      const newcode = this.code9 + "/" + this.itemcode;
      this.appLoading = true;
      axios({
        url: "/item/add",
        method: "POST",
        data: {
          name: this.itemname,
          categoryId: this.catid,
          code: newcode,
          description: this.itemdescription,
          lowStockThreshold: this.itemLowStockThreshold,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.itemadddialogue = false;
            this.resetForm2();
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    editcode(item) {
      this.code1 = item;
      this.code2 = (this.code1 || "").split("/");
      this.code3 = this.code2.pop();
      this.curitem2.code = this.code3;
      this.code4 = this.code2.join("/");
    },
    editcode2() {
      this.code3 = this.curitem2.code;
      this.codeNew = this.code4 + "/" + this.code3;
      this.edititem();
    },
    edititem() {
      this.appLoading = true;
      axios({
        url: "/item/edit",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curitem2._id,
          name: this.curitem2.name,
          code: this.codeNew,
          description: this.curitem2.description,
          lowStockThreshold: this.curitem2.lowStockThreshold,
        },
      })
        .then((response) => {
          this.itemeditdialoge = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.curitem2 = {};
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.getCategory1();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    delitem() {
      this.appLoading = true;
      axios({
        url: "/item/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId2,
        },
      })
        .then((response) => {
          this.itemdeldialoge = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.curId2 = "";
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    // Design CRUD
    openAddDesign(item) {
      this.code8 = item.code;
      this.catid2 = item._id;
      this.resetForm3();
      this.designadddialogue = true;
    },
    openEditDesign(design) {
      this.curitem3 = JSON.parse(JSON.stringify(design));
      if (this.curitem3.HSN && typeof this.curitem3.HSN === "object") {
        this.curitem3.HSN = this.curitem3.HSN._id;
      }
      this.designeditdialoge = true;
      this.editcode3(this.curitem3.code);
    },
    openDeleteDesign(id) {
      this.curId3 = id;
      this.designdeldialoge = true;
    },
    AdddesignValidation() {
      if (!this.designname) {
        this.msg = "Please enter design name";
        this.showsnackbar = true;
      } else if (!this.designcode) {
        this.msg = "Please enter design code";
        this.showsnackbar = true;
      } else if (!this.designcountOrSplit) {
        this.msg = "Please enter design type";
        this.showsnackbar = true;
      } else if (!this.designHSN) {
        this.msg = "Please choose HSN code";
        this.showsnackbar = true;
      } else {
        this.Adddesign();
      }
    },
    editdesignValidation() {
      if (!this.curitem3.name) {
        this.msg = "Please enter design name";
        this.showsnackbar = true;
      } else if (!this.curitem3.code) {
        this.msg = "Please enter design code";
        this.showsnackbar = true;
      } else if (!this.curitem3.countOrSplit) {
        this.msg = "Please enter design type";
        this.showsnackbar = true;
      } else if (!this.curitem3.HSN) {
        this.msg = "Please choose HSN code";
        this.showsnackbar = true;
      } else {
        this.editcode4();
      }
    },
    Adddesign() {
      const newcode = this.code8 + "/" + this.designcode;
      this.appLoading = true;
      axios({
        url: "/design/add",
        method: "POST",
        data: {
          name: this.designname,
          itemId: this.catid2,
          code: newcode,
          description: this.designdescription,
          HSN: this.designHSN,
          countOrSplit: this.designcountOrSplit,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.designadddialogue = false;
            this.resetForm3();
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    editcode3(item) {
      this.coded1 = item;
      this.coded2 = (this.coded1 || "").split("/");
      this.coded3 = this.coded2.pop();
      this.curitem3.code = this.coded3;
      this.coded4 = this.coded2.join("/");
    },
    editcode4() {
      this.coded3 = this.curitem3.code;
      this.codedNew = this.coded4 + "/" + this.coded3;
      this.editdesign();
    },
    editdesign() {
      this.appLoading = true;
      axios({
        url: "/design/edit",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curitem3._id,
          name: this.curitem3.name,
          code: this.codedNew,
          description: this.curitem3.description,
          HSN: this.curitem3.HSN,
          countOrSplit: this.curitem3.countOrSplit,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.designeditdialoge = false;
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.curitem3 = {};
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.getCategory1();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    deldesign() {
      this.appLoading = true;
      axios({
        url: "/design/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId3,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.designdeldialoge = false;
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.curId3 = "";
            this.getCategory1();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    // HSN CRUD & Helpers
    getHSN() {
      axios({
        url: "/hsnCodes/list",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: 1,
          limit: 100,
          searchKeyword: "",
        },
      })
        .then((response) => {
          if (response.data && response.data.data) {
            this.HSNList = response.data.data;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    openAddHsn() {
      this.resetForm4();
      this.addHSNdialog = true;
    },
    AddHsn() {
      if (!this.newHSNcode || !this.newHSNname || !this.newHSNpercent) {
        this.msg = "Please fill in all required HSN fields";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        url: "/hsncode/add",
        method: "post",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          name: this.newHSNname,
          hsnCode: this.newHSNcode,
          hsnDescription: this.newHSNdescription,
          gstSlab1Percentage: this.newHSNpercent,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.showsnackbar = true;
          this.addHSNdialog = false;
          this.msg = response.data.msg || "HSN added successfully";
          this.resetForm4();
          this.getHSN();
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    getHsnDisplay(hsn) {
      if (!hsn) return "—";
      if (typeof hsn === "object" && hsn.hsnCode) {
        return hsn.hsnCode;
      }
      const found = this.HSNList.find((h) => h._id === hsn);
      if (found) {
        return found.hsnCode;
      }
      return hsn;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');

.category-page-wrapper {
  padding: 16px;
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.page-title {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

/* Action Controls */
.search-input-wrap {
  width: 220px;
}

.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0;
  font-size: 13px;
}

.btn-toggle-expand {
  height: 38px !important;
  border-radius: 8px !important;
  font-size: 12px !important;
  border: 1px solid #cbd5e1 !important;
  background: #ffffff;
}

.btn-primary {
  height: 38px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
}

/* Simple 3 KPI Overview Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 2px;
}

/* Master Data Table Card */
.content-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.table-header-row {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.th-label {
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.03em;
}

.category-block {
  border-bottom: 1px solid #f1f5f9;
}

.category-row {
  cursor: pointer;
  background: #ffffff;
  transition: background 0.15s ease;
}

.category-row:hover {
  background: #fafaf9;
}

.category-row-active {
  background: #fafaf9;
  border-bottom: 1px solid #f1f5f9;
}

.td-text {
  font-size: 13.5px;
  color: #0f172a;
}

.code-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: #475569;
}

.count-badge {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
}

.actions-cell {
  gap: 4px;
}

/* Items Container */
.items-container {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  border-left: 2px solid #cbd5e1;
}

.sub-header-row {
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.sub-th-label {
  font-size: 10.5px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.03em;
}

.item-block {
  border-bottom: 1px solid #e2e8f0;
}

.item-block:last-child {
  border-bottom: none;
}

.item-row {
  cursor: pointer;
  background: #ffffff;
  transition: background 0.15s ease;
}

.item-row:hover {
  background: #f8fafc;
}

.item-row-active {
  background: #f8fafc;
}

.td-sub-text {
  font-size: 12.5px;
  color: #1e293b;
}

/* Designs Container */
.designs-container {
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  border-left: 2px solid #94a3b8;
}

.design-header-row {
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}

.design-row {
  background: #ffffff;
  border-bottom: 1px solid #f8fafc;
}

.design-row:last-child {
  border-bottom: none;
}

.simple-tag {
  font-size: 10.5px;
  font-weight: 600;
  background: #f1f5f9;
  color: #475569;
  padding: 1px 5px;
  border-radius: 4px;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.prefix-box {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  padding: 8px 10px;
  border-radius: 6px;
  font-family: monospace;
  font-weight: 700;
  color: #475569;
  font-size: 12px;
}

/* Modal Form Styles */
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 4px;
}

.text-muted {
  color: #64748b;
}

.text-slate-800 {
  color: #0f172a;
  font-weight: 600;
}
</style>
